<template>
  <div class="space-y-6 lg:space-y-8">
    <!-- AI Provider Section (moved to top with separate Save) -->
    <div class="rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4)] p-5 lg:p-6">
      <div class="flex items-center justify-between text-xs lg:text-sm font-medium text-heading">
        <span>{{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.USAGE_LABEL') }}</span>
        <span>{{ aiUsageSummary }}</span>
      </div>
      <div class="relative mt-2 h-2 rounded-full overflow-hidden bg-slate-200 dark:bg-slate-800/60">
        <div class="absolute inset-y-0 left-0 rounded-full transition-all duration-500" :style="usageBarStyle"></div>
      </div>
      <p class="mt-2 text-xs text-body-light" v-if="aiReplyStats.unlimited">
        {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.USAGE_UNLIMITED_HINT') }}
      </p>
      <p class="mt-2 text-xs text-body-light" v-else>
        {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.USAGE_HINT', { percentage: aiUsagePercentageDisplay }) }}
      </p>
    </div>

    <div class="rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.4)] p-5 lg:p-6">
      <div class="flex items-center justify-between mb-4">
        <h4 class="text-base lg:text-md font-medium text-heading">
          {{ $t('AI_SETTINGS.CONFIGURATIONS.TITLE') }}
        </h4>
        <div class="flex items-center gap-2">
          <woot-button
            variant="smooth"
            color-scheme="secondary"
            :loading="isTestingConnection"
            :disabled="isSystemCredentials"
            @click="testConnection"
          >
            {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.TEST_CONNECTION') }}
          </woot-button>
          <woot-button
            class="input-base"
            variant="smooth"
            color-scheme="success"
            :loading="isSavingProvider"
            :disabled="!isProviderDirty"
            icon="save"
            :trailing-icon="true"
            @click="saveProvider"
          >
            {{ $t('GENERAL.SAVE') || 'Save' }}
          </woot-button>
        </div>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 mt-6">
        <div>
          <label class="block text-sm font-medium text-heading mb-2">{{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODE_LABEL') }}</label>
          <select
            v-model="providerForm.credential_mode"
            :class="inputClasses"
            @change="handleCredentialModeChange"
          >
            <option value="system">{{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODE_OPTIONS.SYSTEM') }}</option>
            <option value="customer">{{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODE_OPTIONS.CUSTOM') }}</option>
          </select>
          <p v-if="isSystemCredentials" class="text-xs text-body-light mt-2">
            {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_HINT') }}
          </p>
        </div>

        <div v-if="isSystemCredentials" class="lg:col-span-2">
          <label class="block text-sm font-medium text-heading mb-2">&nbsp;</label>
          <div class="rounded-lg border border-dashed border-hilab/30 dark:border-hilab/30 bg-hilab/5 dark:bg-hilab/10 px-4 py-3 text-sm text-heading">
            {{ systemMessage }}
          </div>
        </div>

        <template v-else>
          <div>
            <label class="block text-sm font-medium text-heading mb-2">{{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODEL') }}</label>
            <select v-model="providerForm.model" :class="inputClasses" @change="onModelChange">
              <option v-for="model in openaiModels" :key="model.value" :value="model.value">
                {{ model.label }}<span v-if="model.popular"> - {{ $t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.MODEL_POPULAR') }}</span>
              </option>
            </select>
            <p v-if="selectedModelNote" class="text-xs text-body-light mt-1">
              {{ selectedModelNote }}
            </p>
          </div>
          <div class="lg:col-span-1" style="display: none;">
            <label class="block text-sm font-medium text-heading mb-2">{{ $t('AI_SETTINGS.API_URL') }}</label>
            <input
              v-model.trim="providerForm.api_url"
              type="text"
              :class="inputClasses"
              autocomplete="off"
              autocorrect="off"
              autocapitalize="off"
              spellcheck="false"
              name="ai_provider_api_url"
              readonly
            />
          </div>
          <div class="lg:col-span-1">
            <label class="block text-sm font-medium text-heading mb-2">{{ $t('AI_SETTINGS.API_KEY') }}</label>
            <div class="flex items-center gap-2">
              <div class="relative w-full">
                <input
                  v-model.trim="providerForm.api_key"
                  type="text"
                  :class="[...inputClasses(), '!pr-8']"
                  :placeholder="$t('AI_SETTINGS.API_KEY_PLACEHOLDER') || 'Enter API key'"
                  :style="providerForm.api_key && !isApiKeyVisible ? { WebkitTextSecurity: 'disc' } : null"
                  autocomplete="off"
                  autocorrect="off"
                  autocapitalize="off"
                  spellcheck="false"
                  name="ai_provider_api_key"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
                <button
                  v-if="providerForm.api_key"
                  type="button"
                  class="absolute text-n-gray-5 bg-n-gray-25/80 dark:bg-n-vars-black-bd/80 dark:text-n-gray-3 rounded-full right-1 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 p-1 z-10"
                  @click="toggleApiKeyVisibility"
                >
                  <lucide-icon :name="isApiKeyVisible ? 'eye-closed' : 'eye'" class="w-4 h-4" />
                </button>
              </div>
              <woot-button
                v-if="providerForm.api_key"
                :title="$t('GENERAL.COPY') || 'Copy'"
                class="hover:opacity-75"
                variant="smooth"
                color-scheme="secondary"
                :disabled="!providerForm.api_key"
                @click="copyApiKey"
              >
                <lucide-icon name="copy" class="w-4 h-4" />
              </woot-button>
            </div>
          </div>
        </template>
      </div>
    </div>

    <div class="rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-slate-900/30 backdrop-blur-md shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.06)] dark:shadow-[0_1px_2px_rgba(0,0,0,0.35),0_8px_24px_rgba(0,0,0,0.4)] p-5 lg:p-6 space-y-8">
      <!-- Header Section -->
      <div class="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3">
        <div class="flex-1">
          <h3 class="text-lg font-medium text-heading truncate">
            {{ $t('AI_SETTINGS.OVERVIEW.TITLE') }}
          </h3>
          <p class="text-sm text-body-light truncate">
            {{ $t('AI_SETTINGS.OVERVIEW.DESCRIPTION') }}
          </p>
        </div>
        <div class="flex items-center justify-between lg:justify-start gap-2 lg:gap-4">
          <div class="text-sm text-body-light whitespace-nowrap">
            {{ $t('AI_SETTINGS.OVERVIEW.BOT_VERSION') }}
          </div>
          <woot-button
            class="input-base flex-shrink-0"
            variant="smooth"
            color-scheme="success"
            :loading="isSaving"
            :disabled="!isBotDirty"
            icon="save"
            :trailing-icon="true"
            @click="saveBotConfiguration"
          >
          {{ $t('AI_SETTINGS.OVERVIEW.SAVE_CHANGES') }}
          </woot-button>
        </div>
      </div>

      <!-- Basic Information Section -->
      <div>
        <h4 class="text-base lg:text-md font-medium text-heading mb-3 lg:mb-4">
          {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.TITLE') }}
        </h4>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <!-- Bot Name -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BOT_NAME') }}
            </label>
            <input
              v-model="botConfig.bot_name"
              type="text"
              :class="inputClasses"
              :placeholder="$t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BOT_NAME_PLACEHOLDER')"
            />
          </div>

          <!-- Business Name -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BUSINESS_NAME') }}
            </label>
            <input
              v-model="botConfig.business_name"
              type="text"
              :class="inputClasses"
              :placeholder="$t('AI_SETTINGS.OVERVIEW.BASIC_INFO.BUSINESS_NAME_PLACEHOLDER')"
            />
          </div>

          <!-- Response Language -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE') }}
            </label>
            <select
              v-model="botConfig.language"
              :class="inputClasses"
            >
              <option value="auto">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.auto') }}</option>
              <option value="en">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.en') }}</option>
              <option value="vi">{{ $t('AI_SETTINGS.OVERVIEW.BASIC_INFO.LANGUAGE_OPTIONS.vi') }}</option>
            </select>
          </div>  
        </div>
      </div>

      <!-- AI Configuration and Behavior Section -->
      <div class="pt-4 border-t border-white/15 dark:border-white/10 space-y-4">
        <h4 class="text-base lg:text-md font-medium text-heading">
          {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TITLE') }}
        </h4>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <!-- Response Speed -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.RESPONSE_SPEED') }}
            </label>
            <input
              v-model.number="botConfig.response_speed"
              type="number"
              min="0"
              max="60"
              :class="inputClasses"
            />
            <p class="text-xs text-body-light mt-1">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.RESPONSE_SPEED_DESCRIPTION') }}
            </p>
          </div>

          <!-- Text Reply Delay -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TEXT_REPLY_DELAY') }}
            </label>
            <input
              v-model.number="botConfig.text_reply_delay"
              type="number"
              min="0"
              max="60"
              :class="inputClasses"
            />
            <p class="text-xs text-body-light mt-1">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TEXT_REPLY_DELAY_DESCRIPTION') }}
            </p>
          </div>

          <!-- Image Reply Delay -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.IMAGE_REPLY_DELAY') }}
            </label>
            <input
              v-model.number="botConfig.image_reply_delay"
              type="number"
              min="0"
              max="120"
              :class="inputClasses"
            />
            <p class="text-xs text-body-light mt-1">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.IMAGE_REPLY_DELAY_DESCRIPTION') }}
            </p>
          </div>

          <!-- Temperature -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TEMPERATURE') }}
            </label>
            <input
              v-model.number="botConfig.temperature"
              type="number"
              min="0"
              max="2"
              step="0.1"
              :class="inputClasses"
            />
            <p class="text-xs text-body-light mt-1">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.TEMPERATURE_DESCRIPTION') }}
            </p>
          </div>

          <!-- Bot Modes Switches -->
          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.PRODUCT_CONSULTING') }}
            </label>
            <woot-switch :model-value="botConfig.product_consulting_enabled" @input="val => botConfig.product_consulting_enabled = val" />
            <p class="text-xs text-body-light mt-1">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.PRODUCT_CONSULTING_DESCRIPTION') }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.CUSTOMER_SUPPORT') }}
            </label>
            <woot-switch :model-value="botConfig.customer_support_enabled" @input="val => botConfig.customer_support_enabled = val" />
            <p class="text-xs text-body-light mt-1">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.CUSTOMER_SUPPORT_DESCRIPTION') }}
            </p>
          </div>

          <!-- Addressing Style -->
          <div class="lg:col-span-2">
            <label class="block text-sm font-medium text-heading mb-2">
              {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE') }}
            </label>
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label class="block text-xs text-body-light mb-1">
                  {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_XUNG') }}
                </label>
                <input
                  v-model="botConfig.addressing_style.xung"
                  type="text"
                  :class="inputClasses"
                  :placeholder="$t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_PLACEHOLDER')"
                />
              </div>
              <div>
                <label class="block text-xs text-body-light mb-1">
                  {{ $t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_HO') }}
                </label>
                <input
                  v-model="botConfig.addressing_style.ho"
                  type="text"
                  :class="inputClasses"
                  :placeholder="$t('AI_SETTINGS.OVERVIEW.AI_BEHAVIOR.ADDRESSING_STYLE_PLACEHOLDER')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Situation Handling Section -->
      <div class="pt-4 border-t border-white/15 dark:border-white/10 space-y-3">
        <h4 class="text-base lg:text-md font-medium text-heading">
          {{ $t('AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.TITLE') }}
        </h4>
        
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <!-- Fallback Message -->
          <div class="lg:col-span-2">
            <p class="block text-xs mb-2 text-body-light">
              {{ $t('AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.FALLBACK_MESSAGE.LABEL') }}
            </p>
            <textarea
              v-model="botConfig.fallback_message"
              :class="[...inputClasses(), 'min-h-[96px]']"
              :placeholder="fallbackPlaceholder"
            ></textarea>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Spacer for mobile bottom bar -->
    <div class="mb-12 lg:mb-0"></div>
</div>
</template>

<script>
import { useAlert } from 'dashboard/composables'

export default {
  name: 'OverviewTab',
  
  props: {
    aiConfiguration: {
      type: Object,
      default: null
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  
  emits: ['refresh'],
  
  data() {
    return {
      isSaving: false,
      isSavingProvider: false,
      isTestingConnection: false,
      connectionStatus: null,
      isApiKeyVisible: false,
      aiReplyStats: { usage: 0, limit: null, percentage: 0, unlimited: true },
      providerForm: { provider: 'openai', api_key: '', api_url: 'https://api.openai.com/v1/chat/completions', model: 'gpt-4o-mini', credential_mode: 'customer', system_message: '' },
      botConfig: {
        bot_name: '',
        business_name: '',
        language: 'auto',
        response_speed: 5,
        text_reply_delay: 1,
        image_reply_delay: 5,
        temperature: 0.6,
        addressing_style: { xung: 'mình', ho: 'bạn' },
        product_consulting_enabled: false,
        customer_support_enabled: false,
        fallback_message: '',
        provider: 'openai',
        model: 'gpt-4o-mini',
        api_key: '',
        api_url: 'https://api.openai.com/v1/chat/completions',
        credential_mode: 'customer',
      },
      openaiModels: [],
      defaultModel: 'gpt-4o-mini'
    }
  },
  
  computed: {
    usageBarStyle() {
      const stats = this.aiReplyStats || {}
      if (stats.unlimited) {
        return {
          width: '100%',
          background: 'linear-gradient(90deg, #2563eb 0%, #7c3aed 100%)'
        }
      }
      const percent = Math.min(100, Math.max(0, stats.percentage || 0))
      let color = '#22c55e'
      if (percent >= 90) {
        color = '#dc2626'
      } else if (percent >= 70) {
        color = '#f97316'
      }
      return {
        width: `${percent}%`,
        backgroundColor: color
      }
    },
    aiUsageSummary() {
      const usage = this.aiReplyStats?.usage || 0
      if (this.aiReplyStats?.unlimited || !this.aiReplyStats?.limit) {
        return `${usage} / ∞`
      }
      return `${usage} / ${this.aiReplyStats.limit}`
    },
    aiUsagePercentageDisplay() {
      if (this.aiReplyStats?.unlimited || !this.aiReplyStats?.limit) {
        return 0
      }
      return Math.min(100, Math.round((this.aiReplyStats.percentage || 0)))
    },
    fallbackDefaultMessage() {
      const fromConfig = this.aiConfiguration?.default_fallback_message
      if (fromConfig) return fromConfig

      const defaultKey = 'AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.FALLBACK_MESSAGE.DEFAULT_SAMPLE'
      const translated = this.$t(defaultKey)
      if (translated && translated !== defaultKey) return translated

      return 'Mình đã nhận thông tin bạn gửi. Để hỗ trợ nhanh và chính xác hơn, bạn mô tả rõ vấn đề/mục tiêu hoặc sản phẩm/dịch vụ quan tâm. Mình đồng thời chuyển thông tin cho nhân viên hỗ trợ sớm nhất.'
    },
    fallbackPlaceholder() {
      const placeholderKey = 'AI_SETTINGS.OVERVIEW.SITUATION_HANDLING.FALLBACK_MESSAGE.PLACEHOLDER'
      const translated = this.$t(placeholderKey, { default: this.fallbackDefaultMessage })
      if (translated && translated !== placeholderKey) return translated

      return this.fallbackDefaultMessage
    },
    availableModels() {
      return this.openaiModels.map(m => m.value)
    },
    selectedModelNote() {
      const selected = this.openaiModels.find(m => m.value === this.providerForm.model)
      return selected?.note || ''
    },
    isProviderDirty() {
      const normalize = value => (value == null ? '' : value)
      return normalize(this.providerForm.model) !== normalize(this.botConfig.model) ||
             normalize(this.providerForm.api_url) !== normalize(this.botConfig.api_url) ||
             normalize(this.providerForm.api_key) !== normalize(this.botConfig.api_key) ||
             normalize(this.providerForm.credential_mode) !== normalize(this.botConfig.credential_mode);
    },
    isSystemCredentials() {
      return this.providerForm.credential_mode === 'system';
    },
    systemMessage() {
      if (!this.isSystemCredentials) return ''
      return this.providerForm.system_message || this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_MESSAGE')
    },
    isBotDirty() {
      const normalize = value => (value == null ? '' : value)
      return this.botConfig.bot_name !== this.aiConfiguration?.bot_name ||
             this.botConfig.business_name !== this.aiConfiguration?.business_name ||
             this.botConfig.language !== this.aiConfiguration?.language ||
             this.botConfig.response_speed !== this.aiConfiguration?.response_speed ||
             this.botConfig.text_reply_delay !== this.aiConfiguration?.text_reply_delay ||
             this.botConfig.image_reply_delay !== this.aiConfiguration?.image_reply_delay ||
             this.botConfig.temperature !== this.aiConfiguration?.temperature ||
             this.botConfig.product_consulting_enabled !== this.aiConfiguration?.product_consulting_enabled ||
             this.botConfig.customer_support_enabled !== this.aiConfiguration?.customer_support_enabled ||
             this.botConfig.addressing_style.xung !== this.aiConfiguration?.addressing_style?.xung ||
             this.botConfig.addressing_style.ho !== this.aiConfiguration?.addressing_style?.ho ||
             normalize(this.botConfig.fallback_message) !== normalize(this.aiConfiguration?.fallback_message) ||
             normalize(this.botConfig.model) !== normalize(this.aiConfiguration?.model) ||
             normalize(this.botConfig.api_key) !== normalize(this.aiConfiguration?.api_key) ||
             normalize(this.botConfig.api_url) !== normalize(this.aiConfiguration?.api_url);
    }
  },
  
  watch: {
    aiConfiguration: {
      handler(newConfig) {
        if (newConfig) {
          this.loadBotConfiguration(newConfig)
        }
      },
      immediate: true
    }
  },
  
  async mounted() {
    await this.loadOpenaiModels()
  },
  
  methods: {
    inputClasses() {
      return [
        'w-full',
        'rounded-lg',
        'bg-white/70',
        'dark:bg-slate-800/60',
        'border',
        'border-hilab/30',
        'dark:border-hilab/30',
        'px-3',
        'py-2.5',
        'text-sm',
        'text-slate-900',
        'dark:text-slate-100',
        'placeholder-slate-400',
        'dark:placeholder-slate-400/80',
        'shadow-sm',
        'focus:outline-none',
        'focus:ring-2',
        'focus:ring-hilab/30',
        'focus:border-hilab'
      ]
    },
    applyAiReplyStats(stats) {
      const usage = Number(stats?.usage || 0)
      const rawLimit = stats?.limit
      const parsedLimit = rawLimit === undefined || rawLimit === null ? null : Number(rawLimit)
      const limit = Number.isFinite(parsedLimit) ? parsedLimit : null
      const unlimited = stats?.unlimited === undefined || stats?.unlimited === null ? (limit === null) : Boolean(stats.unlimited)
      let percentage = stats?.percentage
      if (percentage === undefined || percentage === null) {
        percentage = limit && !unlimited && limit !== 0 ? (usage / limit) * 100 : 0
      }

      this.aiReplyStats = {
        usage,
        limit,
        unlimited,
        percentage: Number.isFinite(percentage) ? Math.min(100, Math.max(0, Number(percentage))) : 0
      }
    },
    toggleApiKeyVisibility() {
      this.isApiKeyVisible = !this.isApiKeyVisible
    },
    async handleCredentialModeChange() {
      if (this.isSystemCredentials) {
        this.providerForm.api_key = ''
        this.providerForm.provider = ''
        this.providerForm.api_url = ''
        this.providerForm.model = ''
        this.providerForm.system_message = this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_MESSAGE')
        this.isApiKeyVisible = false
        this.applyAiReplyStats(this.aiReplyStats)
      } else {
        try {
          const accountId = this.$store.getters.getCurrentAccountId
          const res = await window.axios.get(`/api/v1/accounts/${accountId}/ai_provider`)
          const data = res.data || {}
          this.providerForm.provider = 'openai'
          this.providerForm.api_key = data.api_key || this.providerForm.api_key || ''
          this.providerForm.api_url = data.api_url || this.providerForm.api_url || 'https://api.openai.com/v1/chat/completions'
          this.providerForm.model = data.model || this.providerForm.model || (this.defaultModel || 'gpt-4o-mini')
        } catch (e) {
          // ignore fetch errors, fall back to defaults
        }
        if (!this.providerForm.api_url) {
          this.providerForm.api_url = 'https://api.openai.com/v1/chat/completions'
        }
        if (!this.providerForm.model) {
          this.providerForm.model = this.defaultModel || 'gpt-4o-mini'
        }
        this.providerForm.system_message = ''
      }
    },
    async copyApiKey() {
      try {
        if (!this.providerForm.api_key) return
        await navigator.clipboard.writeText(this.providerForm.api_key)
        const copiedKey = 'GENERAL.COPIED_SUCCESS'
        const copiedMsg = this.$t(copiedKey)
        useAlert(copiedMsg === copiedKey ? 'Copied to clipboard' : copiedMsg, {type: 'info'})
      } catch (e) {
        useAlert(this.$t('GENERAL.COPY_ERROR') || 'Copy failed', {type: 'error'})
      }
    },
    async loadProvider(config = null) {
      try {
        if (config) {
          const mode = config.credential_mode || 'customer'
          const provider = mode === 'system' ? '' : 'openai'
          const apiUrl = mode === 'system' ? '' : (config.api_url || 'https://api.openai.com/v1/chat/completions')
          const model = mode === 'system' ? '' : (config.model || 'gpt-4o-mini')
          const apiKey = mode === 'system' ? '' : (config.api_key || '')
          this.providerForm = {
            provider,
            api_key: apiKey,
            api_url: apiUrl,
            model,
            credential_mode: mode,
            system_message: mode === 'system' ? (config.system_message || this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_MESSAGE')) : ''
          }
          if (config.ai_reply_stats) {
            this.applyAiReplyStats(config.ai_reply_stats)
          }
        } else {
          const accountId = this.$store.getters.getCurrentAccountId
          const res = await window.axios.get(`/api/v1/accounts/${accountId}/ai_provider`)
          const d = res.data || {}
          const mode = d.credential_mode || 'customer'
          const provider = mode === 'system' ? '' : 'openai'
          const apiUrl = mode === 'system' ? '' : (d.api_url || 'https://api.openai.com/v1/chat/completions')
          const model = mode === 'system' ? '' : (d.model || 'gpt-4o-mini')
          const apiKey = mode === 'system' ? '' : (d.api_key || '')
          this.providerForm = {
            provider,
            api_key: apiKey,
            api_url: apiUrl,
            model,
            credential_mode: mode,
            system_message: mode === 'system' ? (d.system_message || this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_MESSAGE')) : ''
          }
        }
      } catch (e) {
        // ignore
      }
    },
    async saveProvider() {
      try {
        this.isSavingProvider = true
        const accountId = this.$store.getters.getCurrentAccountId
        const payload = { 
          ...this.providerForm,
          provider: 'openai',
          api_url: this.providerForm.api_url || 'https://api.openai.com/v1/chat/completions'
        }
        if (this.isSystemCredentials) {
          payload.api_key = null
        }

        const response = await window.axios.put(
          `/api/v1/accounts/${accountId}/ai_provider`,
          { ai_provider: payload }
        )

        const data = response?.data || {}
        const mode = data.credential_mode || payload.credential_mode || 'customer'
        const provider = mode === 'system' ? '' : 'openai'
        const apiUrl = mode === 'system' ? '' : (data.api_url || payload.api_url || 'https://api.openai.com/v1/chat/completions')
        const model = mode === 'system' ? '' : (data.model || payload.model || 'gpt-4o-mini')
        const apiKey = mode === 'system' ? '' : (data.api_key || '')

        this.providerForm = {
          provider,
          api_key: apiKey,
          api_url: apiUrl,
          model,
          credential_mode: mode,
          system_message: mode === 'system' ? (data.system_message || this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_MESSAGE')) : ''
        }

        this.botConfig = {
          ...this.botConfig,
          provider,
          model,
          api_key: apiKey,
          api_url: apiUrl,
          credential_mode: mode
        }

        const savedKey = 'GENERAL.SAVED_SUCCESS'
        const savedMessage = this.$t(savedKey)
        useAlert(savedMessage === savedKey ? 'Saved successfully' : savedMessage, {type: 'success'})
      } catch (e) {
        useAlert(this.$t('GENERAL.SAVE_ERROR') || 'Save failed', {type: 'error'})
      } finally {
        this.isSavingProvider = false
      }
    },
    loadBotConfiguration(config) {
      if (!config) return
      
      const defaultAddressingStyle = { xung: 'mình', ho: 'bạn' }
      const addressingStyle = {
        ...defaultAddressingStyle,
        ...(config.addressing_style || {})
      }

      const mode = config.credential_mode || 'customer'
      const provider = mode === 'system' ? '' : 'openai'
      const apiUrl = mode === 'system' ? '' : (config.api_url || 'https://api.openai.com/v1/chat/completions')
      const model = mode === 'system' ? '' : (config.model || 'gpt-4o-mini')
      const apiKey = mode === 'system' ? '' : (config.api_key || '')

      this.botConfig = {
        bot_name: config.bot_name || '',
        business_name: config.business_name || '',
        language: config.language || 'auto',
        response_speed: config.response_speed || 5,
        text_reply_delay: config.text_reply_delay ?? 1,
        image_reply_delay: config.image_reply_delay ?? 5,
        temperature: config.temperature || 0.6,
        addressing_style: { ...addressingStyle },
        product_consulting_enabled: !!config.product_consulting_enabled,
        customer_support_enabled: !!config.customer_support_enabled,
        fallback_message: config.fallback_message || '',
        provider,
        model,
        api_key: apiKey,
        api_url: apiUrl,
        credential_mode: mode,
      }
      
      // Load provider form from config data
      this.providerForm = {
        provider,
        api_key: apiKey,
        api_url: apiUrl,
        model,
        credential_mode: mode,
        system_message: mode === 'system' ? (config.system_message || this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_MESSAGE')) : ''
      }

      this.applyAiReplyStats(config.ai_reply_stats)
    },
    
    async loadOpenaiModels() {
      try {
        const accountId = this.$store.getters.getCurrentAccountId
        const response = await window.axios.get(`/api/v1/accounts/${accountId}/ai_configurations/openai_models`)
        if (response.data && response.data.models) {
          this.openaiModels = response.data.models
          this.defaultModel = response.data.default || 'gpt-4o-mini'
          
          if (!this.providerForm.model) {
            this.providerForm.model = this.defaultModel
          }
        }
      } catch (e) {
        console.error('Failed to load OpenAI models:', e)
        this.openaiModels = [
          { value: 'gpt-4o-mini', label: 'GPT-4o Mini', popular: true, recommended: true, note: 'Phổ biến: cân bằng tốc độ/chi phí/độ chính xác' }
        ]
        this.defaultModel = 'gpt-4o-mini'
      }
    },
    onModelChange() {
      this.providerForm.api_url = 'https://api.openai.com/v1/chat/completions'
      this.providerForm.provider = 'openai'
    },
    
    async testConnection() {
      if (this.isSystemCredentials) {
        useAlert(this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.SYSTEM_TEST_UNAVAILABLE'), {type: 'alert'})
        return
      }

      if (!this.providerForm.api_key || !this.providerForm.model || !this.providerForm.api_url) {
        useAlert(this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.ERRORS.MISSING_CONFIG'), {type: 'alert'})
        return
      }
      
      try {
        this.isTestingConnection = true
        this.connectionStatus = null
        
        const accountId = this.$store.getters.getCurrentAccountId
        const response = await window.axios.post(
          `/api/v1/accounts/${accountId}/ai_configurations/test_connection`,
          {
            provider: 'openai',
            model: this.providerForm.model,
            api_key: this.providerForm.api_key,
            api_url: this.providerForm.api_url || 'https://api.openai.com/v1/chat/completions'
          }
        )
        
        if (response.data.success) {
          this.connectionStatus = 'success'
          useAlert(this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.CONNECTION_SUCCESS'), {type: 'success'})
        } else {
          this.connectionStatus = 'failed'
          useAlert(response.data.error || this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.CONNECTION_FAILED'), {type: 'error'})
        }
      } catch (error) {
        this.connectionStatus = 'failed'
        const errorMessage = error.response?.data?.error || this.$t('AI_SETTINGS.OVERVIEW.AI_PROVIDER.CONNECTION_FAILED')
        useAlert(errorMessage, {type: 'error'})
      } finally {
        this.isTestingConnection = false
      }
    },
    
    async saveBotConfiguration() {
      try {
        this.isSaving = true
        const accountId = this.$store.getters.getCurrentAccountId
        const fallbackMessage = (this.botConfig.fallback_message || '').trim()
        const payload = {
          bot_name: this.botConfig.bot_name,
          business_name: this.botConfig.business_name,
          language: this.botConfig.language,
          response_speed: this.botConfig.response_speed,
          text_reply_delay: this.botConfig.text_reply_delay,
          image_reply_delay: this.botConfig.image_reply_delay,
          temperature: this.botConfig.temperature,
          addressing_style: this.botConfig.addressing_style,
          product_consulting_enabled: this.botConfig.product_consulting_enabled,
          customer_support_enabled: this.botConfig.customer_support_enabled,
          fallback_message: fallbackMessage.length ? fallbackMessage : null
        }

        await window.axios.put(
          `/api/v1/accounts/${accountId}/ai_configurations/${this.aiConfiguration.id}`,
          { ai_configuration: payload }
        )
        
        const successKey = 'AI_SETTINGS.OVERVIEW.SUCCESS.CONFIG_SAVED'
        const translated = this.$t(successKey)
        const generalSavedKey = 'GENERAL.SAVED_SUCCESS'
        const generalSavedMessage = this.$t(generalSavedKey)
        
        let message
        if (translated === successKey) {
          message = generalSavedMessage === generalSavedKey ? 'Saved successfully' : generalSavedMessage
        } else {
          message = translated
        }
        useAlert(message, {type: 'success'})
        this.$emit('refresh')
      } catch (error) {
        console.error('Error saving bot configuration:', error)
        const errorMessage = error.response?.data?.error || this.$t('AI_SETTINGS.OVERVIEW.ERRORS.SAVE_FAILED')
        useAlert(errorMessage, {type: 'error'})
      } finally {
        this.isSaving = false
      }
    },
  }
}
</script>
